import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { getMyApplications, deleteApplication } from '../api/applications';
import { FileText, Calendar, CheckCircle, XCircle, Clock, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';

const MyApplications = () => {
  const { user } = useAuth();
  const { isExpanded } = useSidebar();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Worker üzerinden verileri çek
    const fetchApplications = async () => {
      try {
        if (!user) {
          setLoading(false);
          return;
        }

        const data = await getMyApplications();
        setApplications(data || []);
        setLoading(false);
      } catch (error) {
        logger.error('Başvurular çekilirken hata:', error);
        toast.error('Başvurular yüklenirken hata oluştu');
        setLoading(false);
      }
    };

    fetchApplications();
  }, [user]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200';
      case 'approved':
      case 'accepted':
        return 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200';
      case 'rejected':
        return 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200';
      case 'completed':
        return 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border border-blue-200';
      default:
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'approved':
      case 'accepted':
        return 'Kabul Edildi';
      case 'rejected':
        return 'Reddedildi';
      case 'completed':
        return 'Tamamlandı';
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    try {
      // Önce hangi uygulamaya ait olduğunu bul
      const application = applications.find(app => app.id === applicationId);
      if (!application) return;

      // Başvuruyu tamamen sil (yeniden başvuru yapabilsin)
      await deleteApplication(applicationId);

      // Başvuru listesinden tamamen kaldır (görünmesin)
      setApplications(applications.filter(app => app.id !== applicationId));

      toast.success('Başvuru geri çekildi!');
    } catch (error) {
      logger.error('Başvuru geri çekilirken hata:', error);
      toast.error('Başvuru geri çekilirken hata oluştu!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="relative">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-emerald-600"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Sparkles className="h-8 w-8 text-emerald-600 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Mobile Header Gradient */}
      <div className="md:hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-48 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Desktop Header Gradient */}
      <div className="hidden md:block bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-48 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className={`relative -mt-32 pb-8 transition-all duration-300 ${
        isExpanded ? 'md:ml-72' : 'md:ml-18'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="backdrop-blur-xl bg-white/20 rounded-2xl p-6 shadow-xl border border-white/30">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Başvurularım</h1>
                  <p className="text-white/80">
                    Tester olarak başvurduğunuz uygulamaları takip edin
                  </p>
                </div>
              </div>
            </div>
          </div>

          {applications.length === 0 ? (
            <div className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg p-12 border border-white/40">
              <div className="text-center">
                <FileText className="mx-auto h-16 w-16 text-emerald-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">Henüz başvuru yok</h3>
                <p className="mt-2 text-gray-600">
                  Teste hazır uygulamalara başvuru yaparak başlayın.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {applications.map((application) => (
                <div key={application.id} className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg p-6 border border-white/40 hover:shadow-xl transition-all duration-300 group">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 
                        className="text-lg font-semibold text-gray-900 mb-3 cursor-pointer hover:text-emerald-700 transition-colors group-hover:text-emerald-700"
                        onClick={() => window.location.href = `/app/${application.app_id}`}
                      >
                        {application.app?.title || 'Bilinmeyen Uygulama'}
                      </h3>
                      <div className="flex items-center space-x-2 mb-3">
                        {getStatusIcon(application.status)}
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(application.status)}`}>
                          {getStatusText(application.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="backdrop-blur-sm bg-white/40 rounded-lg p-3 border border-white/30">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {application.app?.description || 'Açıklama yok'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center">
                        <span className="font-medium text-emerald-700">Platform:</span>
                        <span className="ml-2 px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs">
                          {application.app?.platform || 'Bilinmiyor'}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-teal-600" />
                        <span>{new Date(application.created_at).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => window.location.href = `/app/${application.app_id}`}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Detay
                    </button>
                    <button
                      onClick={() => handleDeleteApplication(application.id)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-red-500 to-rose-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Geri Çek
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyApplications;